/* ============================================================================
   ACEPLORE · module-cart.js — AJAX cart + slide-out drawer + section rendering.
   Custom elements: <lumina-cart-drawer>, <quantity-input>
   ========================================================================== */
(function () {
  'use strict';
  const { utils, events, EVENTS, config } = window.Lumina;

  /* ---- Quantity stepper -------------------------------------------------- */
  class QuantityInput extends window.Lumina.Element {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.min = parseInt(this.dataset.min || '1', 10);
      this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : Infinity;
      this.querySelectorAll('button').forEach((b) =>
        this.listen(b, 'click', () => this.step(b.name === 'plus' ? 1 : -1))
      );
      this.listen(this.input, 'change', () => this.clamp());
    }
    step(dir) {
      const v = (parseInt(this.input.value, 10) || 0) + dir;
      this.input.value = Math.min(this.max, Math.max(this.min, v));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    clamp() {
      let v = parseInt(this.input.value, 10);
      if (isNaN(v)) v = this.min;
      this.input.value = Math.min(this.max, Math.max(this.min, v));
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---- Cart API helpers -------------------------------------------------- */
  const CartAPI = {
    async get() {
      return utils.fetchJSON(config.routes.cart + '.js');
    },
    async add(items, sections) {
      const body = { items: [].concat(items) };
      if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
      return utils.fetchJSON(config.routes.cartAdd, { method: 'POST', body: JSON.stringify(body) });
    },
    async change(payload, sections) {
      if (sections) { payload.sections = sections; payload.sections_url = window.location.pathname; }
      return utils.fetchJSON(config.routes.cartChange, { method: 'POST', body: JSON.stringify(payload) });
    },
    async updateNote(note) {
      return utils.fetchJSON(config.routes.cartUpdate, { method: 'POST', body: JSON.stringify({ note }) });
    },
  };
  window.Lumina.cart = CartAPI;

  const DRAWER_SECTION = 'cart-drawer';

  // Cart type "page"/"popup": no drawer section is rendered, so any leftover
  // [data-cart-open] trigger navigates to the cart page instead of doing nothing.
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cart-open]');
    if (t && !document.querySelector('lumina-cart-drawer')) {
      e.preventDefault();
      window.location.href = config.routes.cart;
    }
  });

  /* ---- Cart drawer element ---------------------------------------------- */
  class CartDrawer extends window.Lumina.Element {
    connectedCallback() {
      this.panel = this.querySelector('.lm-cart-drawer__panel');
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.release = null;
      this.bindStatic();
      this.bindDynamic();
      this._cleanups.push(() => (this._dynCleanups || []).forEach((fn) => fn()));
      // Global triggers
      this.listen(document, 'click', (e) => {
        const trigger = e.target.closest('[data-cart-open]');
        if (trigger) { e.preventDefault(); this.open(); }
      });
      this.sub(EVENTS.CART_OPEN, () => this.open());
      this.sub(EVENTS.CART_UPDATE, (payload) => {
        this.syncCount(payload);
        // A change made elsewhere (cart page) carries no drawer section —
        // refetch ours or the drawer shows stale lines on next open.
        if (payload && payload.sections && payload.sections[DRAWER_SECTION]) return;
        this.refresh();
      });
    }
    bindStatic() {
      // Delegated: the empty-state "Continue shopping" button lives inside
      // [data-cart-body], which render() replaces wholesale — a per-element
      // binding here would go dead after the first re-render.
      this.listen(this, 'click', (e) => { if (e.target.closest('[data-cart-close]')) this.close(); });
      if (this.overlay) this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
    }
    // Re-bound after section re-render (items/qty/remove/note).
    bindDynamic() {
      // Release the previous generation first — its nodes were replaced by
      // render(), so keeping their listeners just pins detached rows in memory
      // for the drawer's whole lifetime.
      (this._dynCleanups || []).forEach((fn) => fn());
      this._dynCleanups = [];
      const dynListen = (el, type, fn) => {
        el.addEventListener(type, fn);
        this._dynCleanups.push(() => el.removeEventListener(type, fn));
      };
      this.querySelectorAll('[data-cart-remove]').forEach((b) =>
        dynListen(b, 'click', () => this.changeLine(b.closest('[data-cart-item]')?.dataset.key, 0, b))
      );
      this.querySelectorAll('[data-cart-item] .lm-qty__input').forEach((input) =>
        dynListen(input, 'change', () => {
          const item = input.closest('[data-cart-item]');
          this.changeLine(item.dataset.key, parseInt(input.value, 10), item);
        })
      );
      const note = this.querySelector('[data-cart-note]');
      if (note) dynListen(note, 'change', utils.debounce(() =>
        CartAPI.updateNote(note.value).catch((err) =>
          Lumina.toast((err && err.message) || utils.t('cartError', config.strings.cartError), { type: 'error' })
        ), 500));
      const checkout = this.querySelector('[data-requires-terms]');
      if (checkout) {
        dynListen(checkout, 'click', (e) => {
          const terms = this.querySelector('[data-cart-terms]');
          if (terms && !terms.checked) { e.preventDefault(); Lumina.toast(utils.t('terms', 'Please accept the terms'), { type: 'error' }); }
        });
      }
      const termsBox = this.querySelector('[data-cart-terms]');
      if (termsBox) {
        const syncExpress = () => this.querySelectorAll('[data-express-checkout]').forEach((w) => {
          w.classList.toggle('is-accepted', termsBox.checked);
          w.toggleAttribute('inert', !termsBox.checked);
          w.setAttribute('aria-disabled', String(!termsBox.checked));
        });
        dynListen(termsBox, 'change', syncExpress);
        syncExpress();
      }
      // Upsells load on first drawer open (see open()) — fetching them for a
      // closed drawer on every page load is wasted work. But if the drawer is
      // already open (post-change re-render), refresh them now.
      if (this.isOpen) {
        const upsell = this.querySelector('[data-upsell-target]');
        if (upsell && !upsell.dataset.loaded) this.loadUpsells(upsell);
      }
    }
    // "You may also like" — Shopify recommendations for the first cart item,
    // minus anything already in the cart, rendered as compact quick-add cards.
    async loadUpsells(el) {
      el.dataset.loaded = 'true';
      const productId = el.dataset.productId;
      if (!productId) { el.closest('[data-cart-upsell]')?.remove(); return; }
      const exclude = (el.dataset.exclude || '').split(',').filter(Boolean).map(Number);
      const root = (config.routes.root || '/').replace(/\/$/, '');
      const url = `${root}/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=${encodeURIComponent(el.dataset.intent || 'related')}`;
      let products = [];
      try {
        const data = await utils.fetchJSON(url);
        products = (data.products || []).filter((p) => !exclude.includes(p.id)).slice(0, 3);
      } catch (_) { /* recommendations are progressive — fail silent */ }
      if (!products.length) { el.closest('[data-cart-upsell]')?.remove(); return; }
      el.textContent = '';
      products.forEach((p) => {
        const variant = (p.variants || []).find((v) => v.available) || p.variants?.[0];
        const hasOptions = (p.variants || []).length > 1;
        const card = document.createElement('div');
        card.className = 'lm-cart-upsell-card';
        const imgSrc = p.featured_image || (p.images && p.images[0]) || '';
        if (imgSrc && /^(https?:)?\/\//.test(imgSrc)) {
          const img = document.createElement('img');
          try {
            const u = new URL(imgSrc, window.location.origin);
            u.searchParams.set('width', '96');
            img.src = u.toString();
          } catch (_) { img.src = imgSrc; }
          img.alt = '';
          img.width = 48; img.height = 48;
          img.loading = 'lazy';
          card.appendChild(img);
        } else {
          card.appendChild(document.createElement('span'));
        }
        const info = document.createElement('a');
        info.className = 'lm-cart-upsell-card__title lm-line-clamp-2';
        info.href = p.url || '#';
        info.textContent = p.title || '';
        const priceEl = document.createElement('span');
        priceEl.className = 'lm-text-xs lm-text-muted';
        priceEl.textContent = ' ' + utils.formatMoney(p.price);
        info.appendChild(document.createElement('br'));
        info.appendChild(priceEl);
        card.appendChild(info);
        const btn = document.createElement(hasOptions ? 'a' : 'button');
        btn.className = 'lm-btn lm-btn--sm lm-btn--secondary lm-cart-upsell-card__add';
        if (hasOptions) {
          btn.href = p.url || '#';
          btn.textContent = utils.t('chooseOptions', 'Choose options');
        } else {
          btn.type = 'button';
          btn.textContent = utils.t('addToCart', config.strings.addToCart);
        }
        if (!hasOptions && !variant) { btn.disabled = true; btn.textContent = utils.t('soldOut', config.strings.soldOut); }
        else if (!hasOptions) {
          btn.addEventListener('click', async () => {
            btn.disabled = true;
            try {
              const res = await CartAPI.add({ id: variant.id, quantity: 1 }, [DRAWER_SECTION]);
              this.render(res);
              requestAnimationFrame(() => this.querySelector('[data-requires-terms], [data-cart-close]')?.focus({ preventScroll: true }));
              const cart = await CartAPI.get();
              if (res.sections) cart.sections = res.sections; // already rendered — skip the handler's refetch
              events.emit(EVENTS.CART_UPDATE, cart);
            } catch (err) {
              btn.disabled = false;
              Lumina.toast((err && err.message) || utils.t('cartError', config.strings.cartError), { type: 'error' });
            }
          });
        }
        card.appendChild(btn);
        el.appendChild(card);
      });
    }
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      utils.lockScroll(true);
      this.overlay.setAttribute('aria-hidden', 'false');
      this.panel.removeAttribute('inert');
      this.panel.setAttribute('aria-hidden', 'false');
      utils.qsa('[data-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      this.release = utils.trapFocus(this.panel, document.activeElement);
      const upsell = this.querySelector('[data-upsell-target]');
      if (upsell && !upsell.dataset.loaded) this.loadUpsells(upsell);
      events.emit('cart:opened');
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      utils.lockScroll(false);
      if (this.release) { this.release(); this.release = null; }
      this.overlay.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
      utils.qsa('[data-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    }
    // Keyed + serialized: line NUMBERS shift as items are removed, so two
    // racing index-based requests can delete the wrong product. item.key is
    // immutable, and the busy gate keeps changes ordered (the re-render swaps
    // the controls anyway, so a dropped mid-flight edit cannot linger).
    async changeLine(key, quantity, el) {
      if (!key || this.busy) return;
      const row = el?.closest('[data-cart-item]');
      const rows = utils.qsa('[data-cart-item]', this);
      const active = document.activeElement;
      const focusState = {
        key,
        index: Math.max(0, rows.indexOf(row)),
        control: active?.matches('[data-cart-remove]') ? 'remove' : active?.matches('.lm-qty__btn') ? active.name : active?.matches('.lm-qty__input') ? 'input' : 'title',
      };
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      if (row) row.classList.add('is-removing');
      try {
        const res = await CartAPI.change({ id: key, quantity }, [DRAWER_SECTION]);
        this.render(res, focusState);
        events.emit(EVENTS.CART_UPDATE, res);
      } catch (err) {
        // fetchJSON surfaces Shopify's own 422 description (e.g. quantity caps).
        Lumina.toast((err && err.message) || utils.t('cartError', config.strings.cartError), { type: 'error' });
        el && el.closest('[data-cart-item]')?.classList.remove('is-removing');
        this.refresh();
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    // Refetch this drawer's section and swap it in (used after external cart
    // changes and after a failed line change, so the UI mirrors the server).
    // Latest-wins: a slow response must never repaint over a newer render.
    async refresh() {
      const seq = (this._renderSeq || 0) + 1;
      this._renderSeq = seq;
      try {
        const data = await utils.fetchSections(DRAWER_SECTION);
        if (this._renderSeq !== seq) return; // superseded while in flight
        if (data && data[DRAWER_SECTION]) this.render({ sections: data });
      } catch (_) { /* non-fatal — next open still shows last server render */ }
    }
    // Swap the drawer body/footer with freshly rendered section HTML.
    render(cartOrSections, focusState) {
      const html = cartOrSections.sections && cartOrSections.sections[DRAWER_SECTION];
      if (!html) return;
      this._renderSeq = (this._renderSeq || 0) + 1; // invalidate in-flight refreshes
      const doc = new DOMParser().parseFromString(html, 'text/html');
      ['[data-cart-body]', '.lm-cart-drawer__footer', '[data-shipping-bar]', '[data-cart-count-bubble]'].forEach((sel) => {
        const next = doc.querySelector(sel);
        const cur = this.querySelector(sel);
        if (next && cur) cur.replaceWith(next);
        else if (next && !cur && sel === '.lm-cart-drawer__footer') {
          this.panel.appendChild(next);
        } else if (!next && cur && sel === '.lm-cart-drawer__footer') {
          cur.remove();
        }
      });
      this.bindDynamic();
      if (focusState && this.isOpen) requestAnimationFrame(() => this.restoreLineFocus(focusState));
    }
    restoreLineFocus(state) {
      const rows = utils.qsa('[data-cart-item]', this);
      let row = rows.find((item) => item.dataset.key === state.key);
      if (!row && rows.length) row = rows[Math.min(state.index, rows.length - 1)];
      let target = null;
      if (row) {
        if (state.control === 'remove') target = row.querySelector('[data-cart-remove]');
        else if (state.control === 'input') target = row.querySelector('.lm-qty__input');
        else if (state.control === 'plus' || state.control === 'minus') target = row.querySelector(`.lm-qty__btn[name="${state.control}"]`);
        else target = row.querySelector('.lm-cart-item__title');
      }
      (target || this.querySelector('[data-requires-terms], [data-cart-close]'))?.focus({ preventScroll: true });
    }
    syncCount(cart) {
      const count = cart.item_count != null ? cart.item_count : 0;
      this.dataset.cartCount = count;
      // Header badge [data-cart-bubble] is synced at module scope (see
      // syncCartBubble) so it also updates for the "page"/"popup" cart types,
      // which render no drawer element.
      const bubble = this.querySelector('[data-cart-count-bubble]');
      if (bubble) bubble.textContent = count;
    }
  }
  if (!customElements.get('lumina-cart-drawer')) customElements.define('lumina-cart-drawer', CartDrawer);

  /* ---- Global add-to-cart (quick add buttons + product forms) ----------- */
  async function addToCart(payload, opts = {}) {
    const sections = config.cartType === 'drawer' ? ['cart-drawer'] : null;
    const res = await CartAPI.add(payload, sections);
    const cart = await CartAPI.get();
    // Carry the freshly rendered drawer section on the event — without it the
    // drawer's CART_UPDATE handler refetches a section we already have.
    if (res.sections) cart.sections = res.sections;
    events.emit(EVENTS.CART_ADD, { added: res, cart });
    events.emit(EVENTS.CART_UPDATE, cart);
    const drawer = document.getElementById('CartDrawer');
    if (drawer && res.sections) drawer.render(res);
    if (config.cartType === 'drawer' && !opts.silent) events.emit(EVENTS.CART_OPEN);
    else Lumina.toast(config.strings.added || 'Added to cart', { type: 'success' });
    return res;
  }
  window.Lumina.addToCart = addToCart;

  // Quick-add buttons on product cards.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.dataset.variantId;
    if (!id) return;
    btn.classList.add('is-loading');
    btn.disabled = true;
    const original = btn.innerHTML;
    // Keep an accessible name through the loading/success swaps — a bare
    // spinner or "✓" leaves the button nameless for screen readers.
    const srSwap = (text) => {
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = text;
      btn.appendChild(sr);
    };
    btn.innerHTML = '<span class="lm-spinner" aria-hidden="true"></span>';
    srSwap((window.Lumina.a11yStrings && window.Lumina.a11yStrings.loading) || 'Loading');
    try {
      await addToCart({ id, quantity: 1 });
      btn.innerHTML = '<span aria-hidden="true">✓</span>';
      srSwap(config.strings.added || 'Added to cart');
      setTimeout(() => { btn.innerHTML = original; btn.disabled = false; btn.classList.remove('is-loading'); }, 1200);
    } catch (err) {
      Lumina.toast(err.message || config.strings.cartError, { type: 'error' });
      btn.innerHTML = original; btn.disabled = false; btn.classList.remove('is-loading');
    }
  });

  // Header cart badge — updated for EVERY cart type. syncCount lives on the
  // drawer element, which only exists when cart_type=='drawer', so the header
  // count must be driven from module scope to stay in sync for "page"/"popup".
  function syncCartBubble(cart) {
    const count = cart && cart.item_count != null ? cart.item_count : 0;
    utils.qsa('[data-cart-bubble]').forEach((b) => {
      b.textContent = count;
      b.hidden = count === 0;
      b.classList.remove('is-bump');
      void b.offsetWidth;
      b.classList.add('is-bump');
    });
  }
  events.on(EVENTS.CART_UPDATE, syncCartBubble);

  // Keep header cart count fresh on load.
  CartAPI.get().then((cart) => events.emit(EVENTS.CART_UPDATE, cart)).catch(() => {});
})();
